/**
 * Export Service
 * API calls for exporting data (CSV, PDF)
 */

import tokenStorage from '../utils/tokenStorage';

// Runtime detection of API URL (similar to WebSocket handling)
const getApiBaseUrl = () => {
  // First check for environment variable (set at build time)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Runtime detection based on current hostname
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';

  if (!isLocalhost) {
    // In production, use same origin
    return `${window.location.origin}/api`;
  }

  // Default for local development
  return 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Helper function to get auth token (dual storage with Safari resilience)
const getAuthToken = () => {
  return tokenStorage.getToken();
};

// Helper function to get active organization ID for multi-org support
const getActiveOrgId = () => {
  const user = tokenStorage.getUser();
  if (!user) return null;
  const org = user.primaryOrganization || user.organization;
  if (!org) return null;
  return org._id || org;
};

// Helper to trigger download from blob - robust cross-browser method
const downloadBlob = (blob, filename) => {
  console.log('[Download] Starting download:', { filename, blobSize: blob.size, blobType: blob.type });

  // Create blob URL
  const blobUrl = window.URL.createObjectURL(blob);
  console.log('[Download] Blob URL created:', blobUrl);

  // Check if navigator.msSaveBlob exists (IE/Edge legacy)
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    console.log('[Download] Using msSaveOrOpenBlob');
    window.navigator.msSaveOrOpenBlob(blob, filename);
    return;
  }

  // Create and configure link element
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';

  // Append to body
  document.body.appendChild(link);
  console.log('[Download] Link appended to body');

  // Trigger click
  link.click();
  console.log('[Download] Link clicked');

  // Cleanup after a short delay
  setTimeout(() => {
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
    window.URL.revokeObjectURL(blobUrl);
    console.log('[Download] Cleanup completed');
  }, 200);
};

// Generic fetch for file downloads
const fetchFile = async (endpoint, filename, fileType = 'text/csv') => {
  const token = getAuthToken();

  console.log('[Export] Starting export:', { endpoint, filename, fileType });
  console.log('[Export] Token exists:', !!token);
  console.log('[Export] API URL:', API_BASE_URL);

  if (!token) {
    throw new Error('Authentication required. Please log in again.');
  }

  const url = `${API_BASE_URL}${endpoint}`;
  console.log('[Export] Full URL:', url);

  try {
    console.log('[Export] Making fetch request...');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Accept': fileType === 'application/pdf' ? 'application/pdf' : 'text/csv, application/json',
    };

    // Include active organization ID for multi-org support
    const activeOrgId = getActiveOrgId();
    if (activeOrgId) {
      headers['x-organization-id'] = activeOrgId;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    console.log('[Export] Response status:', response.status);
    console.log('[Export] Response ok:', response.ok);

    if (!response.ok) {
      // Try to get error message from response
      const contentType = response.headers.get('content-type');
      console.log('[Export] Error content-type:', contentType);
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        console.log('[Export] Error data:', errorData);
        throw new Error(errorData.error || errorData.message || `Export failed: ${response.status}`);
      }
      // Handle specific HTTP status codes
      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      if (response.status === 403) {
        throw new Error('You do not have permission to export this data.');
      }
      if (response.status === 404) {
        throw new Error('Export endpoint not found. Please contact support.');
      }
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    console.log('[Export] Blob size:', blob.size);
    console.log('[Export] Blob type:', blob.type);

    if (blob.size === 0) {
      throw new Error('Export returned empty file');
    }

    console.log('[Export] Triggering download...');
    downloadBlob(blob, filename);
    console.log('[Export] Download triggered successfully');
    return true;
  } catch (error) {
    console.error('[Export] Error:', error);
    // Network errors
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Unable to connect to server. Please check if the server is running.');
    }
    throw error;
  }
};

const exportService = {
  // ==================== CSV EXPORTS ====================

  /**
   * Export organizations to CSV (admin only)
   */
  exportOrganizationsCSV: async () => {
    return fetchFile('/export/organizations/csv', `organizations-${Date.now()}.csv`);
  },

  /**
   * Export users to CSV
   * @param {string} organizationId - Optional organization filter
   */
  exportUsersCSV: async (organizationId = null) => {
    const query = organizationId ? `?organization=${organizationId}` : '';
    return fetchFile(`/export/users/csv${query}`, `users-${Date.now()}.csv`);
  },

  /**
   * Export sessions to CSV
   * @param {Object} params - Optional filters
   * @param {string} params.organization - Organization ID
   * @param {string} params.status - Session status
   */
  exportSessionsCSV: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const query = queryString ? `?${queryString}` : '';
    return fetchFile(`/export/sessions/csv${query}`, `sessions-${Date.now()}.csv`);
  },

  /**
   * Export analytics to CSV
   * @param {number} period - Days period (default: 30)
   */
  exportAnalyticsCSV: async (period = 30) => {
    return fetchFile(`/export/analytics/csv?period=${period}`, `analytics-${period}days-${Date.now()}.csv`);
  },

  /**
   * Export session participants to CSV
   * @param {string} sessionId - Session ID
   */
  exportSessionParticipantsCSV: async (sessionId) => {
    return fetchFile(`/export/sessions/${sessionId}/participants/csv`, `participants-${sessionId}-${Date.now()}.csv`);
  },

  // ==================== PDF EXPORTS ====================

  /**
   * Export session report to PDF
   * @param {string} sessionId - Session ID
   * @param {string} sessionCode - Session code for filename
   */
  exportSessionPDF: async (sessionId, sessionCode = '') => {
    const filename = sessionCode
      ? `session-report-${sessionCode}.pdf`
      : `session-report-${sessionId}.pdf`;
    return fetchFile(`/export/sessions/${sessionId}/pdf`, filename, 'application/pdf');
  },

  /**
   * Export organization report to PDF (admin only)
   * @param {string} organizationId - Organization ID
   */
  exportOrganizationPDF: async (organizationId) => {
    return fetchFile(`/export/organizations/${organizationId}/pdf`, `organization-report-${organizationId}.pdf`, 'application/pdf');
  },

  /**
   * Export session analytics report to PDF (new modern template)
   * Uses the backend puppeteer-based PDF generation
   * @param {string} sessionId - Session ID
   * @param {string} sessionCode - Session code for filename
   */
  exportSessionAnalyticsReportPDF: async (sessionId, sessionCode = '', options = {}) => {
    // The session report PDF embeds the AI Impact Report by default (for every
    // role's "PDF Report" button). Pass { includeAI: false } only if a caller
    // ever needs the plain report. The backend appends the AI pages when an AI
    // report exists/can be generated, and falls back to the plain report if not.
    const includeAI = options.includeAI !== false;
    const filename = sessionCode
      ? `session-analytics-report-${sessionCode}.pdf`
      : `session-analytics-report-${sessionId}.pdf`;
    const query = includeAI ? '?includeAI=true' : '';
    return fetchFile(`/export/sessions/${sessionId}/report-pdf${query}`, filename, 'application/pdf');
  },

  /**
   * Download a participant's completion certificate PDF.
   * Auth: backend gates this with requireSessionParticipantAccess — anonymous
   * QR-code joiners pass the participant_session cookie automatically;
   * logged-in participants/staff pass the JWT.
   * @param {string} sessionId
   * @param {string} participantId
   * @param {string} participantName - used only for the download filename
   */
  exportParticipantCertificatePDF: async (sessionId, participantId, participantName = '') => {
    const url = `${API_BASE_URL}/export/sessions/${sessionId}/certificate/${participantId}`;
    const headers = { Accept: 'application/pdf' };
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || 'Certificate download failed');
    }

    const blob = await response.blob();
    const safeName = (participantName || 'certificate').replace(/[^a-zA-Z0-9-_]/g, '-');
    downloadBlob(blob, `${safeName}-certificate.pdf`);
    return true;
  },

  /**
   * Export participant certificate/report to PDF (public - basic certificate)
   * @param {string} sessionId - Session ID
   * @param {string} email - Participant email
   * @param {string} name - Participant name
   */
  exportParticipantReportPDF: async (sessionId, email, name) => {
    const params = new URLSearchParams();
    if (email) params.append('email', email);
    if (name) params.append('name', name);
    const query = params.toString() ? `?${params.toString()}` : '';

    // Auth required: backend gates this with requireSessionParticipantAccess.
    // Anonymous participants pass the participant_session cookie via
    // credentials: 'include'; logged-in users pass the JWT.
    const headers = { Accept: 'application/pdf' };
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${API_BASE_URL}/export/participant-report/${sessionId}${query}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || 'Export failed');
    }

    const blob = await response.blob();
    downloadBlob(blob, `certificate-${sessionId}.pdf`);
    return true;
  },

  /**
   * Export detailed participant report PDF (public - comprehensive report with all metrics)
   * @param {string} sessionId - Session ID
   * @param {string} participantId - Participant ID
   * @param {string} participantName - Participant name for filename
   */
  exportParticipantDetailedReportPDF: async (sessionId, participantId, participantName = '') => {
    // Auth required: backend now gates this with requireSessionParticipantAccess.
    // Three legitimate paths covered here:
    //   - logged-in staff (super-admin / client / trainer): JWT in Authorization header
    //   - logged-in participant: same JWT, server matches by user-id or email
    //   - anonymous QR-code joiner: relies on the httpOnly participant_session
    //     cookie, which `credentials: 'include'` sends along automatically
    const url = `${API_BASE_URL}/export/sessions/${sessionId}/participant-report/${participantId}`;
    console.log('[Export] Fetching participant detailed report:', url);

    const headers = { Accept: 'application/pdf' };
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const activeOrgId = getActiveOrgId();
    if (activeOrgId) headers['x-organization-id'] = activeOrgId;

    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || 'Export failed');
    }

    const blob = await response.blob();
    const fileName = participantName
      ? `participant-report-${participantName.replace(/\s+/g, '-')}.pdf`
      : `participant-report-${sessionId}.pdf`;
    downloadBlob(blob, fileName);
    return true;
  },

  /**
   * Bundle every participant's report PDF for a session into one zip file.
   * Sequential generation on the server — for a 50-participant session this
   * can take 30-60s, so callers should set a loading state and warn the user.
   * @param {string} sessionId - Session ID
   * @param {string} sessionName - Session name for the zip filename (optional)
   */
  exportAllParticipantReportsZip: async (sessionId, sessionName = '') => {
    const slug = sessionName
      ? sessionName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 40)
      : `session-${sessionId}`;
    const dateSlug = new Date().toISOString().slice(0, 10);
    const filename = `${slug}-reports-${dateSlug}.zip`;
    return fetchFile(`/export/sessions/${sessionId}/participant-reports/zip`, filename, 'application/zip');
  },

  /**
   * Download a real GST-compliant PDF invoice (admin path).
   * @param {string} invoiceId - MongoDB Invoice _id
   * @param {string} invoiceNumber - INV-YYYY-MM-NNN, used as the filename
   */
  exportInvoicePDF: async (invoiceId, invoiceNumber = '') => {
    const safeName = (invoiceNumber || invoiceId).toString().replace(/[^a-zA-Z0-9-_]/g, '-');
    return fetchFile(
      `/organizations/billing/invoices/${invoiceId}/pdf`,
      `${safeName}.pdf`,
      'application/pdf'
    );
  },

  /**
   * Export trainer performance report to PDF
   * @param {string} trainerId - Trainer ID
   * @param {string} trainerName - Trainer name for filename (optional)
   * @param {string} period - Time period for the report (7d, 30d, 90d, all)
   */
  exportTrainerReportPDF: async (trainerId, trainerName = '', period = '30d') => {
    const filename = trainerName
      ? `${trainerName.replace(/\s+/g, '-')}-Performance-Report.pdf`
      : `trainer-report-${trainerId}.pdf`;
    return fetchFile(`/export/trainers/${trainerId}/report/pdf?period=${period}`, filename, 'application/pdf');
  },

  /**
   * Export trainer performance report to CSV
   * @param {string} trainerId - Trainer ID
   * @param {string} trainerName - Trainer name for filename (optional)
   */
  /**
   * Download the AI Impact Report PDF. The backend accepts the report object in the POST body
   * so we can download whatever's currently on screen without a second AI call to regenerate it.
   */
  exportAIImpactReportPDF: async (sessionId, aiReport, session = {}) => {
    const token = getAuthToken();
    if (!token) throw new Error('Authentication required. Please log in again.');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/pdf',
    };
    const activeOrgId = getActiveOrgId();
    if (activeOrgId) headers['x-organization-id'] = activeOrgId;

    const response = await fetch(`${API_BASE_URL}/export/ai-impact-report-pdf`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ aiReport, session }),
    });

    if (!response.ok) {
      const ct = response.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Export failed: ${response.status}`);
      }
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    if (blob.size === 0) throw new Error('Export returned empty file');
    const safeName = String(session?.name || `session-${sessionId}`).replace(/[^a-z0-9\-_]+/gi, '-').slice(0, 60);
    downloadBlob(blob, `AI-Impact-Report-${safeName}-${Date.now()}.pdf`);
    return true;
  },

  exportTrainerReportCSV: async (trainerId, trainerName = '') => {
    const filename = trainerName
      ? `${trainerName.replace(/\s+/g, '-')}-Performance-Report.csv`
      : `trainer-report-${trainerId}.csv`;
    return fetchFile(`/export/trainers/${trainerId}/report/csv`, filename, 'text/csv');
  },
};

export default exportService;
